// Cave84 — Wines hooks (Phase 1 Week 3)
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WineStyle } from '@cave84/shared-types';
import {
  createWine, deleteEmptyWine, getWine, listWines, updateWine,
  type WineCreateInput, type WineUpdateInput
} from '@/services/wines';
import { qk } from '@/services/firestore';
import { useAuthStore } from '@/stores/auth.store';

function assertOwner() {
  const role = useAuthStore.getState().userDoc?.role;
  if (role !== 'owner') throw new Error('Cave84: owner role required.');
}

export function useWines(opts: { style?: WineStyle; producerId?: string } = {}) {
  return useQuery({
    queryKey: opts.producerId
      ? qk.wines.byProducer(opts.producerId)
      : qk.wines.all,
    queryFn: () => listWines(opts),
  });
}

export function useWine(id: string | undefined) {
  return useQuery({
    queryKey: id ? qk.wines.byId(id) : qk.wines.byId('__noop__'),
    queryFn: () => (id ? getWine(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

/** Client-side search over the all-wines cache. Matches cuvee or producer_id. */
export function useWineSearch(term: string, producersMap: Map<string, { name: string }>) {
  const { data: wines } = useWines();
  const lower = term.toLowerCase().trim();
  if (!lower || !wines) return wines ?? [];
  return wines.filter(w => {
    const cuvee = (w.cuvee_name ?? '').toLowerCase();
    const producer = (producersMap.get(w.producer_id)?.name ?? '').toLowerCase();
    return cuvee.includes(lower) || producer.includes(lower);
  });
}

export function useCreateWine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: WineCreateInput) => { assertOwner(); return createWine(input); },
    onSuccess: (_d, input) => {
      void qc.invalidateQueries({ queryKey: qk.wines.all });
      void qc.invalidateQueries({ queryKey: qk.wines.byProducer(input.producer_id) });
    },
  });
}

export function useUpdateWine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: WineUpdateInput }) => {
      assertOwner(); return updateWine(id, patch);
    },
    onSuccess: (_d, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.wines.all });
      void qc.invalidateQueries({ queryKey: qk.wines.byId(id) });
    },
  });
}

/** Delete an EMPTY wine (no bottles in cellar or history) — see services/wines.deleteEmptyWine. */
export function useDeleteWine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (wineId: string) => { assertOwner(); return deleteEmptyWine(wineId); },
    onSuccess: (_d, wineId) => {
      void qc.invalidateQueries({ queryKey: qk.wines.all });
      void qc.removeQueries({ queryKey: qk.wines.byId(wineId) });
      void qc.invalidateQueries({ queryKey: qk.vintages.all });
      void qc.invalidateQueries({ queryKey: qk.bottles.all });
    },
  });
}
